import { useQuery } from "@tanstack/react-query";
import { ArrowDown, ArrowUp, ArrowUpDown, X } from "lucide-react";
import { useMemo, useState } from "react";
import {
  CartesianGrid, ResponsiveContainer, Scatter, ScatterChart,
  Tooltip, XAxis, YAxis, ZAxis,
} from "recharts";

import {
  getComprasMda,
  getComprasMdaDetalle,
  getComprasMdaRap,
  getComprasMdaRapDetalle,
  type ComprasMdaAgrupacion,
  type ComprasMdaItem,
  type ComprasMdaReporte,
  type ComprasMdaSistema,
} from "@/lib/api-mercado";

/*
 * Compras MDA por participante × zona de carga agrupada, agrupables por día /
 * mes / año. Burbujas: X = zona agrupada, Y = precio promedio ponderado de las
 * compras del participante en esa zona (PML del nodo o PND de la zona en el
 * MDA), tamaño = MW comprados. La tabla de abajo comparte los mismos datos.
 *
 * La última columna (RAP) va por su cuenta: es el promedio de compra de cada
 * participante en las horas críticas del AÑO de la fecha elegida — el Top 100
 * de horas con menor margen de /margen-capacidad — y no depende de la
 * agrupación (día/mes/año) del resto de la tabla.
 */

const nf0 = new Intl.NumberFormat("es-MX", { maximumFractionDigits: 0 });
const nf2 = new Intl.NumberFormat("es-MX", {
  minimumFractionDigits: 2, maximumFractionDigits: 2,
});

const MESES = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];
function fmtFecha(iso: string): string {
  const [y, m, d] = iso.split("-").map(Number);
  return `${String(d).padStart(2, "0")} ${MESES[m - 1]} ${y}`;
}
function fmtPeriodo(agr: ComprasMdaAgrupacion, periodo: string): string {
  if (agr === "dia") return fmtFecha(periodo);
  if (agr === "mes") {
    const [y, m] = periodo.split("-").map(Number);
    return `${MESES[m - 1]} ${y}`;
  }
  return periodo;
}

// Paleta para las burbujas (por participante, en orden de MW); el resto gris.
const PALETA = [
  "#4f46e5", "#0ea5e9", "#14b8a6", "#f59e0b", "#ef4444", "#8b5cf6",
  "#ec4899", "#22c55e", "#eab308", "#06b6d4", "#a855f7", "#f97316",
];
const GRIS = "#94a3b8";

const AGRUPACIONES: { key: ComprasMdaAgrupacion; label: string }[] = [
  { key: "dia", label: "Día" },
  { key: "mes", label: "Mes" },
  { key: "anio", label: "Año" },
];

type SortKey = string; // "participante" | "total" | nombre de grupo

// Qué total del pie de tabla se abrió en el modal de distribución.
type DistSel =
  | { tipo: "grupo"; grupo: string }  // total de una zona agrupada
  | { tipo: "total" }                 // gran total (por participante o por zona)
  | { tipo: "rap" };                  // total del RAP

type Burbuja = {
  grupo: string;
  precio: number;
  mw: number;
  participante: string;
  nombre: string;
  color: string;
};

export function ComprasMdaPage() {
  const [agrupacion, setAgrupacion] = useState<ComprasMdaAgrupacion>("dia");
  const [fecha, setFecha] = useState<string>(""); // "" = última publicada
  // "" = los tres sistemas sumados (SIN + BCA + BCS), que es como vivió este
  // reporte desde siempre; el filtro es opcional.
  const [sistema, setSistema] = useState<ComprasMdaSistema>("");
  const [sortKey, setSortKey] = useState<SortKey>("total");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  // Filtros de la gráfica: zonas apagadas (chips) y participantes palomeados
  // en la tabla (vacío = todos).
  const [zonasOff, setZonasOff] = useState<Set<string>>(new Set());
  const [partsSel, setPartsSel] = useState<Set<string>>(new Set());
  // Celda abierta en el modal de detalle (grupo null = columna Total).
  const [detalleSel, setDetalleSel] = useState<{
    participante: string; nombre: string; grupo: string | null;
  } | null>(null);
  // Celda de RAP abierta (desglose hora por hora del promedio).
  const [rapSel, setRapSel] = useState<{ participante: string; nombre: string } | null>(null);
  // Total del pie de tabla abierto en el modal de distribución (quién compone
  // ese número): una zona, el gran total o el RAP.
  const [distSel, setDistSel] = useState<DistSel | null>(null);
  // Participantes (código) que el usuario sacó del reparto en el modal de
  // distribución. Vive aquí para que aplique igual a cualquier total que abra.
  const [ignorados, setIgnorados] = useState<Set<string>>(new Set());

  const toggleZona = (g: string) =>
    setZonasOff((prev) => {
      const next = new Set(prev);
      if (next.has(g)) next.delete(g);
      else next.add(g);
      return next;
    });

  const togglePart = (p: string) =>
    setPartsSel((prev) => {
      const next = new Set(prev);
      if (next.has(p)) next.delete(p);
      else next.add(p);
      return next;
    });

  const q = useQuery({
    queryKey: ["compras-mda", agrupacion, fecha, sistema],
    queryFn: () => getComprasMda(agrupacion, fecha || undefined, sistema),
    staleTime: 30 * 60 * 1000,
  });
  const data = q.data;

  // RAP: promedio de compra en las horas críticas del AÑO de la fecha elegida.
  // Consulta pesada (~30 s la primera vez de cada año) → va aparte para no
  // frenar la tabla, y se cachea por año (día/mes/año del mismo año la reusan).
  const anioSel = data?.fecha.slice(0, 4);
  const rapQ = useQuery({
    queryKey: ["compras-mda-rap", anioSel, sistema],
    queryFn: () => getComprasMdaRap(data!.fecha, sistema || undefined),
    enabled: !!data,
    staleTime: 60 * 60 * 1000,
  });
  const rap = rapQ.data?.rap;

  const handleSort = (k: SortKey) => {
    if (sortKey === k) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(k);
      setSortDir(k === "participante" ? "asc" : "desc");
    }
  };

  const items = useMemo(() => {
    const rows = [...(data?.items ?? [])];
    rows.sort((a, b) => {
      let cmp = 0;
      if (sortKey === "participante") cmp = a.participante.localeCompare(b.participante);
      else if (sortKey === "total") cmp = a.total - b.total;
      else if (sortKey === "rap") cmp = (rap?.[a.participante] ?? 0) - (rap?.[b.participante] ?? 0);
      else cmp = (a.valores[sortKey] ?? 0) - (b.valores[sortKey] ?? 0);
      if (cmp === 0) cmp = b.total - a.total; // desempate estable
      return sortDir === "asc" ? cmp : -cmp;
    });
    return rows;
  }, [data, sortKey, sortDir, rap]);

  // Color por participante según su ranking de MW (los items del backend ya
  // vienen ordenados por total desc); fuera de la paleta → gris.
  const colorPorParticipante = useMemo(() => {
    const map = new Map<string, string>();
    (data?.items ?? []).forEach((it, i) => {
      map.set(it.participante, i < PALETA.length ? PALETA[i] : GRIS);
    });
    return map;
  }, [data]);

  const burbujas = useMemo<Burbuja[]>(() => {
    if (!data) return [];
    const out: Burbuja[] = [];
    for (const it of data.items) {
      if (partsSel.size > 0 && !partsSel.has(it.participante)) continue;
      for (const g of data.grupos) {
        if (zonasOff.has(g)) continue;
        const mw = it.valores[g] ?? 0;
        const precio = it.precios[g];
        if (mw > 0 && precio != null) {
          out.push({
            grupo: g, precio, mw,
            participante: it.participante, nombre: it.nombre,
            color: colorPorParticipante.get(it.participante) ?? GRIS,
          });
        }
      }
    }
    return out;
  }, [data, colorPorParticipante, zonasOff, partsSel]);

  // Palomeo del encabezado: todos / ninguno (vacío = sin filtro = todos). Se
  // cuenta sobre participantes DISTINTOS: un mismo código puede traer dos
  // nombres y por tanto dos filas, pero el filtro es por participante.
  const partsTabla = useMemo(
    () => Array.from(new Set(items.map((i) => i.participante))),
    [items],
  );
  const todosSel = partsTabla.length > 0 && partsSel.size === partsTabla.length;
  const algunoSel = partsSel.size > 0;
  const toggleTodos = () => setPartsSel(todosSel ? new Set() : new Set(partsTabla));

  // Total del RAP: suma de los participantes mostrados (no el total del endpoint,
  // que incluye participantes sin compras en el periodo de la tabla). Sobre
  // participantes DISTINTOS: el RAP va por código y un código puede ocupar dos
  // filas (dos nombres).
  const totalRap = useMemo(
    () => (rap ? partsTabla.reduce((a, p) => a + (rap[p] ?? 0), 0) : 0),
    [rap, partsTabla],
  );

  const leyenda = (data?.items ?? []).slice(0, PALETA.length);

  const pickerValue = fecha || (data?.fecha ?? "");

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">Compras MDA</h1>
          <p className="text-sm text-slate-500 mt-1">
            Demanda fija ofertada en el MDA por participante y zona de carga agrupada.
            Burbuja: precio promedio ponderado ($/MWh, PML del nodo / PND de la zona) y
            tamaño = MW. CENACE publica con ~2 meses de rezago.{" "}
            {sistema ? `Solo el sistema ${sistema}.` : "Suma de SIN + BCA + BCS."}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="inline-flex border rounded-lg p-0.5 bg-white">
            {([["", "Todos"], ["SIN", "SIN"], ["BCA", "BCA"], ["BCS", "BCS"]] as const).map(
              ([k, l]) => (
                <button
                  key={k || "todos"}
                  type="button"
                  onClick={() => setSistema(k)}
                  title={k === "" ? "SIN + BCA + BCS sumados" : `Solo el sistema ${k}`}
                  className={`px-3 py-1.5 rounded-md text-sm ${
                    sistema === k ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"
                  }`}
                >
                  {l}
                </button>
              ),
            )}
          </div>
          <div className="inline-flex border rounded-lg p-0.5 bg-white">
            {AGRUPACIONES.map((a) => (
              <button key={a.key} type="button"
                onClick={() => setAgrupacion(a.key)}
                className={`px-3 py-1.5 rounded-md text-sm ${
                  agrupacion === a.key ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"
                }`}>
                {a.label}
              </button>
            ))}
          </div>
          <label className="text-xs text-slate-500">
            {agrupacion === "dia" ? "Fecha" : agrupacion === "mes" ? "Mes" : "Año"}
            {agrupacion === "anio" ? (
              <select
                value={(pickerValue || "2026").slice(0, 4)}
                onChange={(e) => setFecha(`${e.target.value}-06-15`)}
                className="ml-1.5 border rounded px-2 py-1.5 text-sm bg-white"
              >
                {Array.from({ length: 10 }, (_, i) => 2026 - i).map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            ) : (
              <input
                type={agrupacion === "dia" ? "date" : "month"}
                value={agrupacion === "dia" ? pickerValue : pickerValue.slice(0, 7)}
                max={agrupacion === "dia" ? data?.fecha_max : data?.fecha_max.slice(0, 7)}
                onChange={(e) => setFecha(agrupacion === "dia" ? e.target.value : `${e.target.value}-15`)}
                className="ml-1.5 border rounded px-2 py-1.5 text-sm bg-white"
              />
            )}
          </label>
          {fecha && (
            <button type="button" onClick={() => setFecha("")}
              className="text-xs border rounded px-2.5 py-1.5 hover:bg-slate-50">
              Última disponible
            </button>
          )}
        </div>
      </div>

      {data && (
        <div className="text-xs text-slate-500">
          Mostrando <b className="text-slate-700">{fmtPeriodo(data.agrupacion, data.periodo)}</b>
          {" · "}{data.items.length} participantes
          {" · total "}<b className="text-slate-700">{nf0.format(data.total)} MW</b>
          {" · última fecha publicada: "}{fmtFecha(data.fecha_max)}
        </div>
      )}

      {q.isLoading && (
        <div className="bg-white border rounded-lg p-8 text-center text-slate-500 text-sm">
          Cargando…
          {agrupacion !== "dia" && (
            <div className="text-xs text-slate-400 mt-1">
              Agregando {agrupacion === "mes" ? "el mes" : "el año"} — la primera vez puede
              tardar {agrupacion === "mes" ? "~10 s" : "~1 min"}; después queda en caché.
            </div>
          )}
        </div>
      )}
      {q.isError && (
        <div className="bg-white border rounded-lg p-8 text-center text-red-600 text-sm">
          {(q.error as Error).message}
        </div>
      )}
      {data && data.items.length === 0 && (
        <div className="bg-white border rounded-lg p-8 text-center text-slate-500 text-sm">
          Sin ofertas de compra MDA en {fmtPeriodo(data.agrupacion, data.periodo)}.
        </div>
      )}

      {data && data.items.length > 0 && (
        <div className="bg-white border rounded-lg p-4">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
            <div className="text-sm font-semibold text-slate-800">
              Precio promedio vs zona — tamaño = MW comprados
              {partsSel.size > 0 && (
                <button type="button" onClick={() => setPartsSel(new Set())}
                  className="ml-3 text-[11px] font-normal text-blue-600 border border-blue-200 rounded-full px-2 py-0.5 hover:bg-blue-50">
                  filtrada a {partsSel.size} participante(s) — quitar
                </button>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-1.5">
              {data.grupos.map((g) => {
                const off = zonasOff.has(g);
                return (
                  <button key={g} type="button" onClick={() => toggleZona(g)}
                    title={off ? "Zona oculta — clic para mostrar" : "Clic para ocultar esta zona"}
                    className={`text-[11px] rounded-full px-2.5 py-0.5 border transition ${
                      off ? "bg-white text-slate-400 line-through" : "bg-slate-900 text-white border-slate-900"
                    }`}>
                    {g}
                  </button>
                );
              })}
            </div>
          </div>
          {burbujas.length === 0 && (
            <div className="py-16 text-center text-sm text-slate-400">
              Sin burbujas con los filtros actuales — reactiva zonas o quita el filtro de participantes.
            </div>
          )}
          {burbujas.length > 0 && (
          <ResponsiveContainer width="100%" height={380}>
            <ScatterChart margin={{ top: 12, right: 24, bottom: 4, left: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="grupo" type="category" allowDuplicatedCategory={false}
                tick={{ fontSize: 11, fill: "#475569" }} interval={0} />
              <YAxis dataKey="precio" type="number" width={64}
                tick={{ fontSize: 11, fill: "#475569" }}
                tickFormatter={(v: number) => nf0.format(v)}
                label={{ value: "$/MWh", angle: -90, position: "insideLeft", style: { fontSize: 11, fill: "#64748b" } }}
                domain={["auto", "auto"]} />
              <ZAxis dataKey="mw" type="number" range={[40, 1600]} />
              <Tooltip
                cursor={{ strokeDasharray: "3 3" }}
                content={({ active, payload }) => {
                  if (!active || !payload?.length) return null;
                  const b = payload[0].payload as Burbuja;
                  return (
                    <div className="bg-white border rounded shadow-lg px-3 py-2 text-xs">
                      <div className="font-semibold text-slate-800">
                        {b.participante}{b.nombre ? ` · ${b.nombre}` : ""}
                      </div>
                      <div className="text-slate-600 mt-0.5">
                        {b.grupo} · <b>{nf0.format(b.mw)} MW</b> · promedio{" "}
                        <b>${nf2.format(b.precio)}/MWh</b>
                      </div>
                    </div>
                  );
                }}
              />
              <Scatter data={burbujas} fillOpacity={0.65} strokeOpacity={0.9}
                shape={(props: unknown) => {
                  const p = props as { cx?: number; cy?: number; size?: number; payload?: Burbuja };
                  if (p.cx == null || p.cy == null) return <g />;
                  const r = Math.sqrt((p.size ?? 40) / Math.PI);
                  return (
                    <circle cx={p.cx} cy={p.cy} r={r} fill={p.payload?.color ?? GRIS}
                      fillOpacity={0.6} stroke={p.payload?.color ?? GRIS} strokeWidth={1} />
                  );
                }} />
            </ScatterChart>
          </ResponsiveContainer>
          )}
          <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-[11px] text-slate-600">
            {leyenda.map((it) => (
              <span key={it.participante} className="inline-flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-full inline-block"
                  style={{ background: colorPorParticipante.get(it.participante) }} />
                {it.participante}
              </span>
            ))}
            {data.items.length > PALETA.length && (
              <span className="inline-flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-full inline-block" style={{ background: GRIS }} />
                otros ({data.items.length - PALETA.length})
              </span>
            )}
          </div>
        </div>
      )}

      {data && data.items.length > 0 && (
        <div className="bg-white border rounded-lg overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-100 text-slate-600 sticky top-0">
              <tr>
                <th className="px-2 py-2 w-8 text-center">
                  <input type="checkbox" checked={todosSel}
                    ref={(el) => { if (el) el.indeterminate = algunoSel && !todosSel; }}
                    onChange={toggleTodos}
                    title={todosSel
                      ? "Quitar el filtro (deseleccionar todos)"
                      : "Seleccionar todos los participantes"}
                    className="w-3.5 h-3.5 accent-blue-600 cursor-pointer align-middle" />
                </th>
                <th className="px-3 py-2 font-medium text-right w-10 text-slate-400">#</th>
                <Th col="participante" align="left" current={sortKey} dir={sortDir} onSort={handleSort}>
                  Participante
                </Th>
                {data.grupos.map((g) => (
                  <Th key={g} col={g} align="right" current={sortKey} dir={sortDir} onSort={handleSort}>
                    {g}
                  </Th>
                ))}
                <Th col="total" align="right" current={sortKey} dir={sortDir} onSort={handleSort}>
                  Total
                </Th>
                <th className="px-3 py-2 font-medium text-right whitespace-nowrap"
                  title="Participación de la fila sobre el total de compras del periodo">
                  % del total
                </th>
                <Th col="rap" align="right" current={sortKey} dir={sortDir} onSort={handleSort}
                  title={`RAP (MW): promedio de compra del participante en las ${
                    rapQ.data ? rapQ.data.horas_cubiertas : 100
                  } horas críticas${anioSel ? ` de ${anioSel}` : ""} — las de menor margen de capacidad`}>
                  RAP (MW)
                </Th>
              </tr>
            </thead>
            <tbody className="tabular-nums">
              {items.map((it, idx) => (
                <Fila key={`${it.participante}|${it.nombre}`} it={it} grupos={data.grupos} num={idx + 1}
                  granTotal={data.total}
                  rap={rap?.[it.participante]} rapCargando={rapQ.isLoading}
                  onRap={() => setRapSel({ participante: it.participante, nombre: it.nombre })}
                  sel={partsSel.has(it.participante)} onToggle={() => togglePart(it.participante)}
                  onCelda={(grupo) => setDetalleSel({
                    participante: it.participante, nombre: it.nombre, grupo,
                  })} />
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-slate-300 bg-slate-50 font-semibold">
                <td className="px-3 py-2" colSpan={3}>
                  TOTAL
                  <span className="ml-2 text-[11px] font-normal text-slate-400">
                    clic en un total = distribución
                  </span>
                </td>
                {data.grupos.map((g) => {
                  const v = data.total_grupo[g] ?? 0;
                  return (
                    <td key={g} className={`px-3 py-2 text-right tabular-nums ${v === 0 ? "text-slate-300" : ""}`}>
                      {v === 0 ? "—" : (
                        <button type="button" onClick={() => setDistSel({ tipo: "grupo", grupo: g })}
                          title={`Ver cómo se reparte el total de ${g} entre participantes`}
                          className="hover:underline decoration-dotted underline-offset-2 hover:text-blue-700 tabular-nums">
                          {nf0.format(v)}
                        </button>
                      )}
                    </td>
                  );
                })}
                <td className="px-3 py-2 text-right tabular-nums">
                  <button type="button" onClick={() => setDistSel({ tipo: "total" })}
                    title="Ver cómo se reparte el total entre participantes y entre zonas"
                    className="hover:underline decoration-dotted underline-offset-2 hover:text-blue-700 tabular-nums">
                    {nf0.format(data.total)}
                  </button>
                </td>
                <td className="px-3 py-2 text-right tabular-nums">100.00%</td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {rap ? (
                    <button type="button" onClick={() => setDistSel({ tipo: "rap" })}
                      title="Ver cómo se reparte el RAP entre participantes"
                      className="hover:underline decoration-dotted underline-offset-2 hover:text-blue-700 tabular-nums">
                      {nf2.format(totalRap)}
                    </button>
                  ) : "—"}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      <p className="text-[11px] text-slate-400">
        Cruce: ofertas_compra_mda → participante (mapa de códigos MDA, 5 primeros caracteres
        con vigencia) → zona de carga (directa o vía nodos_p) → zona agrupada. Precio promedio
        ponderado por MW con pml_mda (ofertas a nodo) o pnd_mda (ofertas a zona). "(sin
        identificar)" = códigos sin mapa vigente para esa fecha. Clic en un número = detalle
        por nodo/zona de carga.
      </p>
      <p className="text-[11px] text-slate-400">
        <b className="text-slate-500">RAP</b>: promedio de la demanda fija ofertada por el
        participante en las <b>horas críticas del año</b> (fecha-hora del Top{" "}
        {rapQ.data?.top ?? 100} de menor margen de capacidad, sistema{" "}
        {(rapQ.data?.sistemas ?? ["SIN"]).join(", ")} — el mismo de Margen de capacidad).
        Es un valor ANUAL: no cambia con la agrupación día/mes/año, solo con el año de la
        fecha elegida.
        {rapQ.isLoading && " Calculándolo… (~30 s la primera vez de cada año)."}
        {rapQ.isError && (
          <span className="text-red-500"> No se pudo calcular: {(rapQ.error as Error).message}</span>
        )}
        {rapQ.data && (
          <> Año <b className="text-slate-500">{rapQ.data.anio}</b>: {rapQ.data.horas_cubiertas}
            {" de "}{rapQ.data.horas} horas críticas con ofertas publicadas
            {rapQ.data.desde && <> ({fmtFecha(rapQ.data.desde)} — {fmtFecha(rapQ.data.hasta!)})</>}
            {rapQ.data.horas_cubiertas < rapQ.data.horas && (
              <span className="text-amber-600">
                {" "}— las posteriores al {fmtFecha(rapQ.data.fecha_max)} aún no publican ofertas
                y quedan fuera del promedio.
              </span>
            )}
          </>
        )}
      </p>

      {rapSel && data && (
        <RapDetalleModal
          sel={rapSel}
          fecha={data.fecha}
          sistema={sistema}
          onClose={() => setRapSel(null)}
        />
      )}

      {distSel && data && (
        <DistribucionModal
          sel={distSel}
          data={data}
          rap={rap}
          rapInfo={rapQ.data ? { anio: rapQ.data.anio, horas: rapQ.data.horas_cubiertas } : undefined}
          colorPorParticipante={colorPorParticipante}
          periodo={fmtPeriodo(data.agrupacion, data.periodo)}
          ignorados={ignorados}
          onIgnorados={setIgnorados}
          onClose={() => setDistSel(null)}
          onParticipante={(participante, nombre, grupo) => {
            setDistSel(null);
            setDetalleSel({ participante, nombre, grupo });
          }}
          onRap={(participante, nombre) => {
            setDistSel(null);
            setRapSel({ participante, nombre });
          }}
        />
      )}

      {detalleSel && data && (
        <DetalleModal
          sel={detalleSel}
          agrupacion={agrupacion}
          fecha={data.fecha}
          sistema={sistema}
          periodo={fmtPeriodo(data.agrupacion, data.periodo)}
          onClose={() => setDetalleSel(null)}
        />
      )}
    </div>
  );
}

function DetalleModal({ sel, agrupacion, fecha, sistema, periodo, onClose }: {
  sel: { participante: string; nombre: string; grupo: string | null };
  agrupacion: ComprasMdaAgrupacion;
  fecha: string;
  sistema: ComprasMdaSistema;
  periodo: string;
  onClose: () => void;
}) {
  const q = useQuery({
    queryKey: ["compras-mda-detalle", agrupacion, fecha, sistema, sel.participante, sel.grupo],
    queryFn: () => getComprasMdaDetalle({
      participante: sel.participante, agrupacion, fecha, grupo: sel.grupo, sistema,
    }),
    staleTime: 30 * 60 * 1000,
  });
  const d = q.data;

  return (
    <div className="fixed inset-0 z-50 bg-black/30 flex items-center justify-center p-4"
      onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b flex items-start justify-between gap-3">
          <div>
            <div className="font-semibold text-slate-900">
              {sel.participante}
              {sel.nombre && <span className="font-normal text-slate-500 text-sm ml-2">{sel.nombre}</span>}
            </div>
            <div className="text-xs text-slate-500 mt-0.5">
              {sel.grupo ?? "Todas las zonas"} · {periodo}
              {d && <>{" · "}{nf0.format(d.total_mw)} MW
                {d.precio_prom != null && <> · promedio ${nf2.format(d.precio_prom)}/MWh</>}</>}
            </div>
          </div>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-700">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="overflow-y-auto">
          {q.isLoading && (
            <div className="p-8 text-center text-sm text-slate-500">Cargando detalle…</div>
          )}
          {q.isError && (
            <div className="p-8 text-center text-sm text-red-600">{(q.error as Error).message}</div>
          )}
          {d && (
            <table className="w-full text-sm">
              <thead className="bg-slate-100 text-slate-600 text-left sticky top-0">
                <tr>
                  <th className="px-4 py-2 font-medium">Zona de carga</th>
                  <th className="px-4 py-2 font-medium">Punto de oferta</th>
                  <th className="px-4 py-2 font-medium">Tipo</th>
                  {agrupacion !== "dia" && <th className="px-4 py-2 font-medium text-right">Días</th>}
                  <th className="px-4 py-2 font-medium text-right">MW</th>
                  <th className="px-4 py-2 font-medium text-right">Precio prom. ($/MWh)</th>
                </tr>
              </thead>
              <tbody className="tabular-nums">
                {d.filas.map((f) => (
                  <tr key={f.nodo_zona} className="border-t hover:bg-slate-50">
                    <td className="px-4 py-1.5">{f.zona_carga}</td>
                    <td className="px-4 py-1.5 font-mono text-xs">{f.nodo_zona}</td>
                    <td className="px-4 py-1.5">
                      <span className={`text-[11px] rounded-full px-2 py-0.5 ${
                        f.es_nodo ? "bg-indigo-100 text-indigo-700" : "bg-emerald-100 text-emerald-700"
                      }`}>
                        {f.es_nodo ? "Nodo (PML)" : "Zona (PND)"}
                      </span>
                    </td>
                    {agrupacion !== "dia" && (
                      <td className="px-4 py-1.5 text-right text-slate-500">{f.dias}</td>
                    )}
                    <td className="px-4 py-1.5 text-right">{nf2.format(f.mw)}</td>
                    <td className="px-4 py-1.5 text-right">
                      {f.precio_prom != null ? nf2.format(f.precio_prom) : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-slate-300 bg-slate-50 font-semibold">
                  <td className="px-4 py-2" colSpan={agrupacion !== "dia" ? 4 : 3}>TOTAL</td>
                  <td className="px-4 py-2 text-right tabular-nums">{nf2.format(d.total_mw)}</td>
                  <td className="px-4 py-2 text-right tabular-nums">
                    {d.precio_prom != null ? nf2.format(d.precio_prom) : "—"}
                  </td>
                </tr>
              </tfoot>
            </table>
          )}
        </div>
        <div className="px-5 py-2 border-t text-[11px] text-slate-400">
          MW = suma de demanda fija ofertada en el periodo. Precio promedio ponderado por MW
          (PML del nodo o PND de la zona, MDA).
        </div>
      </div>
    </div>
  );
}

/**
 * El cálculo del RAP, hora por hora: las horas críticas del año (Top-N de menor
 * margen) con lo que el participante ofertó en cada una. Se muestran TODAS,
 * incluidas las de 0 MW, porque todas cuentan en el divisor.
 */
function RapDetalleModal({ sel, fecha, sistema, onClose }: {
  sel: { participante: string; nombre: string };
  fecha: string;
  sistema: ComprasMdaSistema;
  onClose: () => void;
}) {
  const q = useQuery({
    queryKey: ["compras-mda-rap-detalle", fecha.slice(0, 4), sistema, sel.participante],
    queryFn: () => getComprasMdaRapDetalle(sel.participante, fecha, sistema || undefined),
    staleTime: 60 * 60 * 1000,
  });
  const d = q.data;

  return (
    <div className="fixed inset-0 z-50 bg-black/30 flex items-center justify-center p-4"
      onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b flex items-start justify-between gap-3">
          <div>
            <div className="font-semibold text-slate-900">
              RAP · {sel.participante}
              {sel.nombre && <span className="font-normal text-slate-500 text-sm ml-2">{sel.nombre}</span>}
            </div>
            <div className="text-xs text-slate-500 mt-0.5">
              {d ? (
                <>Horas críticas de <b className="text-slate-700">{d.anio}</b> (Top {d.top} de
                  menor margen, {d.sistemas.join(", ")}) · ofertó en{" "}
                  {d.horas_con_compra} de {d.filas.length}</>
              ) : "Cargando…"}
            </div>
          </div>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-700">
            <X className="w-4 h-4" />
          </button>
        </div>

        {d && (
          <div className="px-5 py-2.5 bg-slate-50 border-b text-sm text-slate-700 tabular-nums">
            <b>{nf2.format(d.suma_mw)} MW</b>
            <span className="text-slate-500"> (suma de las {d.filas.length} horas)</span>
            {" ÷ "}<b>{d.filas.length}</b>
            <span className="text-slate-500"> horas</span>
            {" = "}
            <b className="text-blue-700">{d.rap != null ? nf2.format(d.rap) : "—"} MW</b>
            <span className="text-slate-500"> de RAP</span>
          </div>
        )}

        <div className="overflow-y-auto">
          {q.isLoading && (
            <div className="p-8 text-center text-sm text-slate-500">
              Calculando el desglose…
              <div className="text-xs text-slate-400 mt-1">
                La primera vez de cada año tarda ~30 s; después es inmediato.
              </div>
            </div>
          )}
          {q.isError && (
            <div className="p-8 text-center text-sm text-red-600">{(q.error as Error).message}</div>
          )}
          {d && (
            <table className="w-full text-sm">
              <thead className="bg-slate-100 text-slate-600 text-left sticky top-0">
                <tr>
                  <th className="px-4 py-2 font-medium text-right w-10" title="Lugar en el top de horas críticas del año (1 = la más apretada)">#</th>
                  <th className="px-4 py-2 font-medium">Fecha</th>
                  <th className="px-4 py-2 font-medium text-right">Hora</th>
                  <th className="px-4 py-2 font-medium text-right" title="Capacidad ofertada al MTR − demanda real de esa hora">
                    Margen (MW)
                  </th>
                  <th className="px-4 py-2 font-medium text-right">% reserva</th>
                  <th className="px-4 py-2 font-medium text-right" title="Demanda fija que el participante ofertó en el MDA en esa hora">
                    MW comprados
                  </th>
                </tr>
              </thead>
              <tbody className="tabular-nums">
                {d.filas.map((f) => (
                  <tr key={`${f.fecha}|${f.hora}`}
                    className={`border-t hover:bg-slate-50 ${f.mw === 0 ? "text-slate-400" : ""}`}>
                    <td className="px-4 py-1.5 text-right text-slate-400">{f.pos}</td>
                    <td className="px-4 py-1.5">{fmtFecha(f.fecha)}</td>
                    <td className="px-4 py-1.5 text-right">{f.hora}</td>
                    <td className="px-4 py-1.5 text-right text-slate-500">
                      {f.margen_mw != null ? nf0.format(f.margen_mw) : "—"}
                    </td>
                    <td className="px-4 py-1.5 text-right text-slate-500">
                      {f.margen_pct != null ? `${nf2.format(f.margen_pct)}%` : "—"}
                    </td>
                    <td className={`px-4 py-1.5 text-right ${f.mw > 0 ? "font-medium text-slate-800" : ""}`}>
                      {nf2.format(f.mw)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-slate-300 bg-slate-50 font-semibold">
                  <td className="px-4 py-2" colSpan={5}>SUMA</td>
                  <td className="px-4 py-2 text-right tabular-nums">{nf2.format(d.suma_mw)}</td>
                </tr>
                <tr className="bg-slate-50 font-semibold text-blue-700">
                  <td className="px-4 py-2" colSpan={5}>RAP (÷ {d.filas.length} horas)</td>
                  <td className="px-4 py-2 text-right tabular-nums">
                    {d.rap != null ? nf2.format(d.rap) : "—"}
                  </td>
                </tr>
              </tfoot>
            </table>
          )}
        </div>

        <div className="px-5 py-2 border-t text-[11px] text-slate-400">
          Las horas críticas son las de MENOR margen (capacidad ofertada al MTR − demanda real)
          del año, el mismo Top que publica Margen de capacidad; la #1 es la más apretada.
          Se listan todas —incluidas aquellas en las que el participante no ofertó, con 0 MW—
          porque todas cuentan en el divisor.
          {d && d.horas_cubiertas < d.horas && (
            <span className="text-amber-600">
              {" "}De las {d.horas} horas del top, {d.horas - d.horas_cubiertas} son posteriores
              al {fmtFecha(d.fecha_max)} y aún no publican ofertas: quedan fuera.
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

// Una barra de la distribución: quién (o qué zona) y cuánto del total.
type DistFila = {
  key: string;
  etiqueta: string;
  sub?: string;
  valor: number;
  precio?: number | null;
  color: string;
  participante?: string;
  nombre?: string;
  ignorado?: boolean; // desmarcado por el usuario: fuera del total y del reparto
};

// Azul único (magnitud, no identidad) para la vista por zona: más oscuro = más MW.
const AZUL_ZONA = "37, 99, 235";

/**
 * Distribución de un total del pie de tabla: una barra apilada al 100 % con la
 * composición y, debajo, el ranking con MW, % del total y acumulado. Los
 * participantes conservan el color que tienen en la gráfica de burbujas.
 *
 * Cada participante se puede desmarcar para IGNORARLO: sale del total y el
 * reparto se recalcula con los demás (p.ej. ver la distribución sin CFE SSB).
 * En la vista por zona los ignorados se descuentan de cada zona.
 */
function DistribucionModal({
  sel, data, rap, rapInfo, colorPorParticipante, periodo, ignorados, onIgnorados,
  onClose, onParticipante, onRap,
}: {
  sel: DistSel;
  data: ComprasMdaReporte;
  rap: Record<string, number> | undefined;
  rapInfo?: { anio: number; horas: number };
  colorPorParticipante: Map<string, string>;
  periodo: string;
  ignorados: Set<string>;                 // códigos de participante fuera del reparto
  onIgnorados: (s: Set<string>) => void;
  onClose: () => void;
  onParticipante: (participante: string, nombre: string, grupo: string | null) => void;
  onRap: (participante: string, nombre: string) => void;
}) {
  // El gran total se puede ver repartido entre participantes o entre zonas.
  const [modo, setModo] = useState<"participante" | "zona">("participante");
  const porZona = sel.tipo === "total" && modo === "zona";

  const toggleIgnorado = (p: string) => {
    const next = new Set(ignorados);
    if (next.has(p)) next.delete(p);
    else next.add(p);
    onIgnorados(next);
  };

  const filas = useMemo<DistFila[]>(() => {
    const colorDe = (p: string) => colorPorParticipante.get(p) ?? GRIS;
    let out: DistFila[] = [];
    if (sel.tipo === "grupo" || (sel.tipo === "total" && modo === "participante")) {
      const g = sel.tipo === "grupo" ? sel.grupo : null;
      for (const it of data.items) {
        const valor = g == null ? it.total : (it.valores[g] ?? 0);
        if (valor <= 0) continue;
        out.push({
          key: `${it.participante}|${it.nombre}`,
          etiqueta: it.participante, sub: it.nombre,
          valor, precio: g == null ? it.precio_prom : it.precios[g],
          color: colorDe(it.participante),
          participante: it.participante, nombre: it.nombre,
          ignorado: ignorados.has(it.participante),
        });
      }
    } else if (sel.tipo === "total") {
      // Por zona, descontando a los participantes ignorados; precio promedio de
      // la zona ponderado por los MW que traen precio.
      for (const g of data.grupos) {
        let mw = 0, mwp = 0, mwcp = 0;
        for (const it of data.items) {
          if (ignorados.has(it.participante)) continue;
          const v = it.valores[g] ?? 0, p = it.precios[g];
          mw += v;
          if (v > 0 && p != null) { mwp += v * p; mwcp += v; }
        }
        if (mw > 0) {
          out.push({ key: g, etiqueta: g, valor: mw, precio: mwcp > 0 ? mwp / mwcp : null, color: "" });
        }
      }
    } else {
      // RAP: un valor por participante (código), aunque tenga dos nombres.
      const vistos = new Set<string>();
      for (const it of data.items) {
        if (vistos.has(it.participante)) continue;
        vistos.add(it.participante);
        const v = rap?.[it.participante] ?? 0;
        if (v > 0) {
          out.push({
            key: it.participante, etiqueta: it.participante, sub: it.nombre, valor: v,
            color: colorDe(it.participante),
            participante: it.participante, nombre: it.nombre,
            ignorado: ignorados.has(it.participante),
          });
        }
      }
    }
    out.sort((a, b) => b.valor - a.valor);
    if (sel.tipo === "total" && modo === "zona") {
      out = out.map((f, i) => ({
        ...f, color: `rgba(${AZUL_ZONA}, ${Math.max(0.25, 1 - i * 0.1).toFixed(2)})`,
      }));
    }
    return out;
  }, [sel, data, rap, modo, colorPorParticipante, ignorados]);

  // Solo las filas activas cuentan para el total, la barra y los porcentajes.
  const activas = filas.filter((f) => !f.ignorado);
  const total = activas.reduce((a, f) => a + f.valor, 0);
  const max = activas[0]?.valor ?? 0;
  // Cuántos de los mayores hacen falta para juntar el 80 % del total.
  let acum = 0, n80 = 0;
  for (const f of activas) { acum += f.valor; n80 += 1; if (acum / total >= 0.8) break; }

  // Qué se está ignorando (chips con nombre; en la vista por zona no hay fila
  // por participante, así que el nombre se busca en los items).
  const nombreDe = (p: string) => data.items.find((it) => it.participante === p)?.nombre ?? "";
  const listaIgnorados = Array.from(ignorados).sort();
  const mwIgnorado = porZona
    ? data.items.filter((it) => ignorados.has(it.participante)).reduce((a, it) => a + it.total, 0)
    : filas.filter((f) => f.ignorado).reduce((a, f) => a + f.valor, 0);

  const titulo = sel.tipo === "grupo" ? `Total ${sel.grupo}`
    : sel.tipo === "total" ? "Total de compras" : "Total del RAP";
  const unidad = sel.tipo === "rap" ? "MW de RAP" : "MW";
  const nfv = sel.tipo === "rap" ? nf2 : nf0;
  const nfPct = (v: number) => `${nf2.format(v * 100)}%`;

  // Barra apilada: 8 segmentos + "otros" (paleta fija, el resto se pliega).
  const TOP = 8;
  const segmentos = activas.slice(0, TOP);
  const restoMw = activas.slice(TOP).reduce((a, f) => a + f.valor, 0);

  return (
    <div className="fixed inset-0 z-50 bg-black/30 flex items-center justify-center p-4"
      onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b flex items-start justify-between gap-3">
          <div>
            <div className="font-semibold text-slate-900">
              Distribución · {titulo}
            </div>
            <div className="text-xs text-slate-500 mt-0.5 tabular-nums">
              {sel.tipo === "rap"
                ? <>Horas críticas{rapInfo ? <> de <b className="text-slate-700">{rapInfo.anio}</b> ({rapInfo.horas} h)</> : ""}</>
                : periodo}
              {" · "}<b className="text-slate-700">{nfv.format(total)} {unidad}</b>
              {" · "}{activas.length} {porZona ? "zonas" : "participantes"}
              {activas.length > 1 && total > 0 && (
                <> · los <b className="text-slate-700">{n80}</b> mayores concentran el 80 %</>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {sel.tipo === "total" && (
              <div className="inline-flex border rounded-lg p-0.5 bg-white text-xs">
                {(["participante", "zona"] as const).map((m) => (
                  <button key={m} type="button" onClick={() => setModo(m)}
                    className={`px-2.5 py-1 rounded-md ${
                      modo === m ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"
                    }`}>
                    {m === "participante" ? "Por participante" : "Por zona"}
                  </button>
                ))}
              </div>
            )}
            <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-700">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Participantes fuera del reparto: chips para devolverlos uno a uno o todos. */}
        {listaIgnorados.length > 0 && (
          <div className="px-5 py-2 border-b bg-amber-50 text-xs text-amber-900 flex flex-wrap items-center gap-1.5 tabular-nums">
            <span>
              Ignorando <b>{listaIgnorados.length}</b> participante{listaIgnorados.length === 1 ? "" : "s"}
              {mwIgnorado > 0 && <> ({nfv.format(mwIgnorado)} {unidad} fuera del reparto)</>}:
            </span>
            {listaIgnorados.map((p) => (
              <button key={p} type="button" onClick={() => toggleIgnorado(p)}
                title={`${nombreDe(p) || p} — clic para volver a incluirlo`}
                className="inline-flex items-center gap-1 rounded-full border border-amber-300 bg-white px-2 py-0.5 text-amber-900 hover:bg-amber-100 line-through decoration-amber-400">
                {p}
                <X className="w-3 h-3 no-underline" />
              </button>
            ))}
            <button type="button" onClick={() => onIgnorados(new Set())}
              className="ml-auto text-amber-800 underline decoration-dotted underline-offset-2 hover:text-amber-950">
              Restablecer todos
            </button>
          </div>
        )}

        {filas.length === 0 && (
          <div className="p-8 text-center text-sm text-slate-500">
            {listaIgnorados.length > 0 ? "Todo el total está en participantes ignorados." : "Sin datos que repartir."}
          </div>
        )}

        {filas.length > 0 && (
          <>
            {/* Composición: una barra = el total, cada segmento = su parte. */}
            <div className="px-5 pt-4 pb-3 border-b">
              {activas.length === 0 ? (
                <div className="text-xs text-slate-500 text-center py-2">
                  Todos los participantes están ignorados — vuelve a marcar alguno.
                </div>
              ) : (
                <>
                  <div className="flex h-6 w-full rounded overflow-hidden bg-slate-100">
                    {segmentos.map((f) => (
                      <div key={f.key}
                        title={`${f.etiqueta}${f.sub ? ` · ${f.sub}` : ""}: ${nfv.format(f.valor)} ${unidad} (${nfPct(f.valor / total)})`}
                        className="h-full box-border border-r-2 border-white last:border-r-0"
                        style={{ width: `${(f.valor / total) * 100}%`, background: f.color }} />
                    ))}
                    {restoMw > 0 && (
                      <div title={`Otros (${activas.length - TOP}): ${nfv.format(restoMw)} ${unidad} (${nfPct(restoMw / total)})`}
                        className="h-full" style={{ width: `${(restoMw / total) * 100}%`, background: GRIS, opacity: 0.55 }} />
                    )}
                  </div>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-[11px] text-slate-600">
                    {segmentos.map((f) => (
                      <span key={f.key} className="inline-flex items-center gap-1.5 tabular-nums">
                        <span className="w-2.5 h-2.5 rounded-full inline-block" style={{ background: f.color }} />
                        {f.etiqueta} <span className="text-slate-400">{nfPct(f.valor / total)}</span>
                      </span>
                    ))}
                    {restoMw > 0 && (
                      <span className="inline-flex items-center gap-1.5 tabular-nums">
                        <span className="w-2.5 h-2.5 rounded-full inline-block" style={{ background: GRIS, opacity: 0.55 }} />
                        otros ({activas.length - TOP}) <span className="text-slate-400">{nfPct(restoMw / total)}</span>
                      </span>
                    )}
                  </div>
                </>
              )}
            </div>

            {/* Ranking: barras proporcionales al mayor, con MW, % y acumulado. */}
            <div className="overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-100 text-slate-600 text-left sticky top-0">
                  <tr>
                    {!porZona && (
                      <th className="px-2 py-2 w-8 text-center font-medium"
                        title="Desmarca un participante para sacarlo del reparto">
                        <span className="sr-only">Incluir</span>
                      </th>
                    )}
                    <th className="px-3 py-2 font-medium text-right w-10">#</th>
                    <th className="px-3 py-2 font-medium">{porZona ? "Zona agrupada" : "Participante"}</th>
                    <th className="px-2 py-2 font-medium w-[34%]"></th>
                    <th className="px-3 py-2 font-medium text-right whitespace-nowrap">{unidad}</th>
                    <th className="px-3 py-2 font-medium text-right whitespace-nowrap">% del total</th>
                    <th className="px-3 py-2 font-medium text-right whitespace-nowrap"
                      title="Suma de los porcentajes hasta esta fila">Acum.</th>
                    {sel.tipo !== "rap" && (
                      <th className="px-3 py-2 font-medium text-right whitespace-nowrap"
                        title="Precio promedio ponderado por MW ($/MWh)">Prom. $/MWh</th>
                    )}
                  </tr>
                </thead>
                <tbody className="tabular-nums">
                  {(() => {
                    let acumulado = 0, pos = 0;
                    return filas.map((f) => {
                      if (!f.ignorado) { acumulado += f.valor; pos += 1; }
                      const clic = f.participante != null;
                      const abrir = () => {
                        if (!clic) return;
                        if (sel.tipo === "rap") onRap(f.participante!, f.nombre ?? "");
                        else onParticipante(f.participante!, f.nombre ?? "", sel.tipo === "grupo" ? sel.grupo : null);
                      };
                      return (
                        <tr key={f.key}
                          className={`border-t ${clic ? "hover:bg-slate-50 cursor-pointer" : ""} ${f.ignorado ? "text-slate-400" : ""}`}
                          onClick={abrir}
                          title={clic ? (sel.tipo === "rap" ? "Ver el cálculo hora por hora" : "Ver el detalle por nodo/zona de carga") : undefined}>
                          {!porZona && (
                            <td className="px-2 py-1.5 text-center" onClick={(e) => e.stopPropagation()}>
                              <input type="checkbox" checked={!f.ignorado}
                                onChange={() => f.participante && toggleIgnorado(f.participante)}
                                title={f.ignorado ? "Volver a incluirlo en el reparto" : "Ignorarlo: sale del total y el reparto se recalcula"}
                                className="w-3.5 h-3.5 accent-blue-600 cursor-pointer align-middle" />
                            </td>
                          )}
                          <td className="px-3 py-1.5 text-right text-slate-400">{f.ignorado ? "—" : pos}</td>
                          <td className="px-3 py-1.5 whitespace-nowrap">
                            <span className={`inline-flex items-center gap-2 ${f.ignorado ? "line-through decoration-slate-300" : ""}`}>
                              <span className="w-2.5 h-2.5 rounded-full inline-block shrink-0"
                                style={{ background: f.color, opacity: f.ignorado ? 0.35 : 1 }} />
                              <span className={`font-medium ${f.ignorado ? "" : "text-slate-800"}`}>{f.etiqueta}</span>
                              {f.sub && <span className={`text-xs ${f.ignorado ? "" : "text-slate-500"}`}>{f.sub}</span>}
                            </span>
                            {f.ignorado && (
                              <span className="ml-2 text-[10px] uppercase tracking-wide rounded px-1.5 py-0.5 bg-slate-100 text-slate-500">
                                ignorado
                              </span>
                            )}
                          </td>
                          <td className="px-2 py-1.5">
                            {!f.ignorado && (
                              <div className="h-3.5 rounded-r bg-slate-100 w-full">
                                <div className="h-full rounded-r"
                                  style={{ width: `${max > 0 ? (f.valor / max) * 100 : 0}%`, background: f.color }} />
                              </div>
                            )}
                          </td>
                          <td className={`px-3 py-1.5 text-right ${f.ignorado ? "" : "font-medium text-slate-800"}`}>{nfv.format(f.valor)}</td>
                          <td className="px-3 py-1.5 text-right text-slate-700">
                            {f.ignorado || total <= 0 ? "—" : nfPct(f.valor / total)}
                          </td>
                          <td className="px-3 py-1.5 text-right text-slate-400">
                            {f.ignorado || total <= 0 ? "—" : nfPct(acumulado / total)}
                          </td>
                          {sel.tipo !== "rap" && (
                            <td className="px-3 py-1.5 text-right text-slate-500">
                              {f.precio != null ? nf2.format(f.precio) : "—"}
                            </td>
                          )}
                        </tr>
                      );
                    });
                  })()}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-slate-300 bg-slate-50 font-semibold">
                    <td className="px-3 py-2" colSpan={porZona ? 3 : 4}>
                      TOTAL{listaIgnorados.length > 0 && <span className="font-normal text-slate-500"> sin ignorados</span>}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">{nfv.format(total)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">100.00%</td>
                    <td></td>
                    {sel.tipo !== "rap" && <td></td>}
                  </tr>
                </tfoot>
              </table>
            </div>
          </>
        )}

        <div className="px-5 py-2 border-t text-[11px] text-slate-400">
          {sel.tipo === "rap"
            ? "RAP por participante: promedio de su demanda fija ofertada en las horas críticas del año. Clic en una fila = cálculo hora por hora."
            : porZona
              ? "MW = demanda fija ofertada en el MDA en el periodo, sumada por zona de carga agrupada (sin los participantes ignorados). Precio ponderado por MW con precio (PML del nodo o PND de la zona)."
              : "MW = demanda fija ofertada en el MDA en el periodo. Los colores son los de la gráfica de burbujas (los participantes fuera del Top 12 van en gris). Clic en una fila = detalle por nodo/zona de carga."}
          {" "}Desmarca un participante para ignorarlo: sale del total y el reparto se recalcula con los demás; la selección se conserva al abrir otros totales.
        </div>
      </div>
    </div>
  );
}

function Fila({ it, grupos, num, granTotal, rap, rapCargando, sel, onToggle, onCelda, onRap }: {
  it: ComprasMdaItem; grupos: string[]; num: number; granTotal: number;
  rap: number | undefined; rapCargando: boolean; sel: boolean;
  onToggle: () => void; onCelda: (grupo: string | null) => void; onRap: () => void;
}) {
  return (
    <tr className={`border-t hover:bg-slate-50 ${sel ? "bg-blue-50/60" : ""}`}>
      <td className="px-2 py-1.5 text-center">
        <input type="checkbox" checked={sel} onChange={onToggle}
          title="Filtrar este participante en la gráfica"
          className="w-3.5 h-3.5 accent-blue-600 cursor-pointer" />
      </td>
      <td className="px-3 py-1.5 text-right tabular-nums text-slate-400">{num}</td>
      <td className="px-3 py-1.5 whitespace-nowrap">
        <span className="font-medium text-slate-800">{it.participante}</span>
        {it.nombre && <span className="text-xs text-slate-500 ml-2">{it.nombre}</span>}
      </td>
      {grupos.map((g) => {
        const v = it.valores[g] ?? 0;
        const p = it.precios[g];
        return (
          <td key={g} className={`px-3 py-1.5 text-right ${v === 0 ? "text-slate-300" : ""}`}>
            {v === 0 ? "—" : (
              <button type="button" onClick={() => onCelda(g)}
                title={`Ver detalle${p != null ? ` · promedio $${nf2.format(p)}/MWh` : ""}`}
                className="hover:underline decoration-dotted underline-offset-2 hover:text-blue-700 tabular-nums">
                {nf2.format(v)}
              </button>
            )}
          </td>
        );
      })}
      <td className="px-3 py-1.5 text-right font-semibold">
        <button type="button" onClick={() => onCelda(null)}
          title={`Ver detalle (todas las zonas)${it.precio_prom != null ? ` · promedio $${nf2.format(it.precio_prom)}/MWh` : ""}`}
          className="hover:underline decoration-dotted underline-offset-2 hover:text-blue-700 tabular-nums">
          {nf2.format(it.total)}
        </button>
      </td>
      <td className="px-3 py-1.5 text-right text-slate-500">
        {granTotal > 0 ? `${nf2.format((it.total / granTotal) * 100)}%` : "—"}
      </td>
      <td className="px-3 py-1.5 text-right tabular-nums">
        {rapCargando ? <span className="text-slate-300">…</span> : (
          <button type="button" onClick={onRap}
            title="Ver el cálculo: hora por hora de las horas críticas del año"
            className={`hover:underline decoration-dotted underline-offset-2 hover:text-blue-700 tabular-nums ${
              rap == null ? "text-slate-300" : ""
            }`}>
            {rap != null ? nf2.format(rap) : "—"}
          </button>
        )}
      </td>
    </tr>
  );
}

function Th({ col, align, current, dir, onSort, title, children }: {
  col: string;
  align: "left" | "right";
  current: string;
  dir: "asc" | "desc";
  onSort: (k: string) => void;
  title?: string;
  children: React.ReactNode;
}) {
  const isActive = current === col;
  const Icon = !isActive ? ArrowUpDown : dir === "asc" ? ArrowUp : ArrowDown;
  return (
    <th
      className={`px-3 py-2 font-medium select-none cursor-pointer hover:bg-slate-200 text-${align}`}
      onClick={() => onSort(col)}
      title={title}
    >
      <span className={`inline-flex items-center gap-1 ${align === "right" ? "justify-end" : ""}`}>
        {children}
        <Icon className={`w-3 h-3 shrink-0 ${isActive ? "" : "opacity-40"}`} aria-hidden="true" />
      </span>
    </th>
  );
}
